import { Injectable, inject } from '@angular/core'
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpHeaders,
} from '@angular/common/http'
import { Observable, of, throwError, from } from 'rxjs'
import { environment } from '../../../environments/environment'
import { mergeMap, switchMap, catchError, withLatestFrom } from 'rxjs/operators'
import { Store, select } from '@ngrx/store'
import { AppState } from 'src/app/core/store'
import { selectAuthState } from '../../core/store/auth/auth.selectors'
import { AppInsightsService } from 'src/app/core/telemetry/app-insights.service'
import { InteractionTrackingService } from 'src/app/core/telemetry/interaction-tracking.service'
import { getUserTimezone, getRegionFromTimezone } from 'src/app/core/telemetry/region-mapping'
import { AuthService } from 'src/app/auth.service'

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private store = inject<Store<AppState>>(Store)
  private appInsights = inject(AppInsightsService)
  private interactions = inject(InteractionTrackingService)
  private authService = inject(AuthService)

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      req.url.includes(environment.internalApi.base) &&
      !(req.method === 'GET' && req.url.includes(environment.internalApi.azureb2c)) &&
      !(req.method === 'GET' && req.url.includes(environment.internalApi.appInsightsConfig))
    ) {
      return of(1).pipe(
        withLatestFrom(this.store.pipe(select(selectAuthState))),
        mergeMap(([_, authState]) => {
          if (authState.token && !this.tokenExpired(authState.token)) {
            return next.handle(req.clone({ headers: this.buildHeaders(req, authState.token) }))
          }
          return this.refreshAndRetry(req, next)
        }),
      )
    } else if (req.url.includes(environment.api.base)) {
      return of(1).pipe(
        withLatestFrom(this.store.pipe(select(selectAuthState))),
        mergeMap(([_, authState]) => {
          return next.handle(
            req.clone({
              headers: req.headers.set('Authorization', `Basic ${authState.analyzeReToken}`),
            }),
          )
        }),
      )
    } else {
      return next.handle(req)
    }
  }

  private refreshAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return from(this.authService.getAccessToken(true)).pipe(
      switchMap((newToken) => {
        if (!newToken) {
          window.location.href = '/'
          return throwError(() => new Error('Session expired'))
        }
        return next.handle(req.clone({ headers: this.buildHeaders(req, newToken) }))
      }),
      catchError(() => {
        window.location.href = '/'
        return throwError(() => new Error('Session expired'))
      }),
    )
  }

  private buildHeaders(req: HttpRequest<unknown>, token: string): HttpHeaders {
    let headers = req.headers.set('Authorization', 'Bearer ' + token)
    if (this.appInsights.loginSessionId) {
      headers = headers.set('x-login-session-id', this.appInsights.loginSessionId)
    }
    const activeInteraction = this.interactions.current
    if (activeInteraction) {
      headers = headers.set('x-interaction-name', activeInteraction)
    }
    const tz = getUserTimezone()
    headers = headers.set('X-User-Timezone', tz)
    headers = headers.set('X-User-Region', getRegionFromTimezone(tz))
    return headers
  }

  private tokenExpired(token: string): boolean {
    try {
      const expiry = JSON.parse(atob(token.split('.')[1])).exp
      return Math.floor(Date.now() / 1000) >= expiry
    } catch {
      return true
    }
  }
}
